import { useState, useEffect } from 'react';
import { useToast, useTheme } from '../hooks';
import { profileService } from '../services';
import { Button, LoadingSpinner } from '../components';

const Settings = () => {
  const { showSuccess, showError } = useToast();
  const { themePreference, setTheme } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [preferences, setPreferences] = useState(null);

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const data = await profileService.getPreferences();
        setPreferences(data);
      } catch (err) {
        showError('Failed to load settings');
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();
  }, []);

  const handleChange = (field, value) => {
    setPreferences(prev => ({ ...prev, [field]: value }));
  };

  const handleThemeChange = (value) => {
    setTheme(value);
    handleChange('theme', value);
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      await profileService.updatePreferences(preferences);
      showSuccess('Settings saved successfully');
    } catch (err) {
      showError(err.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <LoadingSpinner size="lg" />
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <h1>Settings</h1>

      <div className="card" style={{ maxWidth: '600px', marginTop: 'var(--spacing-6)' }}>
        <div className="card-header">
          <h3 className="card-title">Preferences</h3>
        </div>

        <div className="form-group">
          <label className="form-label">Theme</label>
          <select
            className="form-input"
            value={themePreference}
            onChange={(e) => handleThemeChange(e.target.value)}
          >
            <option value="light">Light</option>
            <option value="dark">Dark</option>
            <option value="system">System</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Dashboard Layout</label>
          <select
            className="form-input"
            value={preferences?.dashboardLayout || 'grid'}
            onChange={(e) => handleChange('dashboardLayout', e.target.value)}
          >
            <option value="grid">Grid</option>
            <option value="list">List</option>
            <option value="compact">Compact</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Date Format</label>
          <select
            className="form-input"
            value={preferences?.dateFormat || 'MM/DD/YYYY'}
            onChange={(e) => handleChange('dateFormat', e.target.value)}
          >
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Time Format</label>
          <select
            className="form-input"
            value={preferences?.timeFormat || '12h'}
            onChange={(e) => handleChange('timeFormat', e.target.value)}
          >
            <option value="12h">12 Hour</option>
            <option value="24h">24 Hour</option>
          </select>
        </div>

        <Button
          onClick={handleSave}
          loading={isSaving}
          style={{ marginTop: 'var(--spacing-4)' }}
        >
          Save Settings
        </Button>
      </div>
    </div>
  );
};

export default Settings;
